import parse, {Element, attributesToProps, domToReact} from 'html-react-parser'
import sanitizeHtml from 'sanitize-html'

import {twMerge} from '@sdox/utils'

import {Headline} from '../Headline'
import {TextLink} from '../TextLink'

import type {ComponentProps, ReactElement} from 'react'
import type {DOMNode} from 'html-react-parser'

export interface CmsContentProps extends ComponentProps<'div'> {
  content: string
}

const options = {
  replace: (domNode: DOMNode) => {
    if (domNode instanceof Element) {
      const children = domToReact(domNode.children, options)
      const props = attributesToProps(domNode.attribs)

      const elements: Record<string, ReactElement> = {
        h1: (
          <Headline
            size={1}
            tag={'h1'}
            className={'my-2'}
            {...props}
          >
            {children}
          </Headline>
        ),
        h2: (
          <Headline
            size={2}
            tag={'h2'}
            className={'my-2'}
            {...props}
          >
            {children}
          </Headline>
        ),
        h3: (
          <Headline
            size={3}
            tag={'h3'}
            className={'my-1'}
            {...props}
          >
            {children}
          </Headline>
        ),
        h4: (
          <Headline
            size={4}
            tag={'h4'}
            className={'my-1'}
            {...props}
          >
            {children}
          </Headline>
        ),
        h5: (
          <Headline
            size={5}
            tag={'h5'}
            className={'my-1'}
            {...props}
          >
            {children}
          </Headline>
        ),
        i: (
          <em
            className={'italic'}
            {...props}
          >
            {children}
          </em>
        ),
        em: (
          <em
            className={'italic'}
            {...props}
          >
            {children}
          </em>
        ),
        b: (
          <strong
            className={'font-bold'}
            {...props}
          >
            {children}
          </strong>
        ),
        strong: (
          <strong
            className={'font-bold'}
            {...props}
          >
            {children}
          </strong>
        ),
        ul: (
          <ul
            className={'list-disc'}
            {...props}
          >
            {children}
          </ul>
        ),
        ol: (
          <ol
            className={'list-decimal'}
            {...props}
          >
            {children}
          </ol>
        ),
        li: <li {...props}>{children}</li>,
        p: <p className={'mb-2'}>{children}</p>,
        a: (
          <TextLink
            {...props}
            className={twMerge(props.class, 'text-size-inherit')}
          >
            {children}
          </TextLink>
        ),
      }

      return elements[domNode.name]
    }
  },
}

export const sanitize = (html: string) => {
  return sanitizeHtml(html, {
    allowedAttributes: {
      a: ['href', 'target', 'class'],
      b: [],
      strong: [],
      i: [],
      em: [],
    },
  })
}

export const CmsContent = ({content, ...restProps}: CmsContentProps) => (
  <div {...restProps}>{parse(sanitize(content), options)}</div>
)
